import { startAuthentication } from "@simplewebauthn/browser";
import { useEffect, useRef, useState } from "react";
import { api, onStepUp } from "./api.ts";
import { offerDevicePasskey } from "./device-passkey.tsx";
import { Button, ErrorText, Input } from "./ui.tsx";

/**
 * The step-up dialog: sensitive actions (deleting the organization, changing members, sign-in
 * settings, exports, auto-send) ask for a code from the authenticator app or a passkey, then
 * carry on. Mounted once, at the app root.
 */
export function StepUpDialog() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(
    () =>
      onStepUp(
        () =>
          new Promise<boolean>((resolve) => {
            resolver.current = resolve;
            setCode("");
            setError(null);
            setOpen(true);
          }),
      ),
    [],
  );
  useEffect(() => {
    if (open) dialog.current?.showModal();
    else dialog.current?.close();
  }, [open]);

  const finish = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpen(false);
  };
  const withCode = async () => {
    setBusy(true);
    try {
      await api.post("/auth/step-up", { code: code.trim() });
      finish(true);
      void offerDevicePasskey();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const withPasskey = async () => {
    setBusy(true);
    try {
      const optionsJSON = await api.post<Parameters<typeof startAuthentication>[0]["optionsJSON"]>(
        "/auth/mfa/passkey/options",
      );
      const response = await startAuthentication({ optionsJSON });
      await api.post("/auth/mfa/passkey", { response });
      finish(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <dialog
      ref={dialog}
      aria-labelledby="stepup-title"
      onCancel={(e) => {
        e.preventDefault();
        finish(false);
      }}
      className="m-auto w-[min(420px,calc(100vw-32px))] rounded-card border border-line bg-surface p-6 text-ink shadow-pop backdrop:bg-black/40"
    >
      <h2 id="stepup-title" className="font-display text-lg font-semibold tracking-tight">
        Confirm it's you
      </h2>
      <p className="mt-1 text-sm text-muted">
        Enter a code from your authenticator app, or use your passkey. It covers the next 15
        minutes.
      </p>
      <form
        className="mt-4 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim().length >= 6) withCode();
        }}
      >
        <Input
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Authenticator code"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" busy={busy} disabled={code.trim().length < 6}>
            Confirm
          </Button>
          <Button type="button" busy={busy} onClick={withPasskey}>
            Use a passkey
          </Button>
          <Button type="button" variant="ghost" onClick={() => finish(false)}>
            Cancel
          </Button>
        </div>
        <ErrorText error={error} />
      </form>
    </dialog>
  );
}
